import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  FileDown,
  FileUp,
  Info,
  Plus,
  Search,
  Send,
  Trash2,
  Undo2,
  Wand2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tooltip } from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { usePermission } from '../../hooks/usePermission';
import {
  ADD_PLAN_CONSTRAINT_MUTATION,
  CLEAR_PLAN_ASSIGNMENT_MUTATION,
  CONFIRM_PLAN_CONSTRAINT_MUTATION,
  CREATE_STAFFING_PLAN_MUTATION,
  DELETE_PLAN_CONSTRAINT_MUTATION,
  DELETE_STAFFING_PLAN_MUTATION,
  EXPORT_STAFFING_PLAN_MUTATION,
  GENERATE_STAFFING_PLAN_MUTATION,
  GET_PLANNING_AI_AVAILABLE,
  GET_STAFFING_PLAN_BOARD,
  PROPOSE_PLAN_CONSTRAINTS_FROM_TEXT_MUTATION,
  REJECT_PLAN_CONSTRAINT_MUTATION,
  PUBLISH_STAFFING_PLAN_MUTATION,
  SET_PLAN_ASSIGNMENT_MUTATION,
  SET_PLAN_ASSIGNMENT_PIN_MUTATION,
  UNPUBLISH_STAFFING_PLAN_MUTATION,
} from '../../graphql/queries';
import type {
  AddPlanConstraintInput,
  PlanAiProposalResult,
  PlanConstraintInfo,
  PlanGenerateMode,
  PlanViolation,
  StaffingPlanBoard,
  StaffingPlanStatus,
} from './types';
import { GenerateCounts, GeneratePlanDialog } from './GeneratePlanDialog';
import { PlanConstraintsPanel } from './PlanConstraintsPanel';
import { PlanImportDialog } from './PlanImportDialog';
import { downloadBase64Xlsx } from './planExcel';
import { buildBoardModel, cellKey, exceptionTargets, groupEmployees } from './boardModel';
import {
  DateMeta,
  PlanBoard,
  cellDomId,
  headcountDomId,
  positionDomId,
  rowDomId,
} from './PlanBoard';
import { PlanCellEditor } from './PlanCellEditor';
import { PlanConfirmDialog } from './PlanConfirmDialog';
import { ViolationsPanel } from './ViolationsPanel';
import { PlanLegend } from './PlanLegend';
import {
  addDaysToDateString,
  errorMessage,
  formatTimestamp,
  isDateString,
  mondayOf,
  mondayOfDateString,
  parseDateString,
  todayDateString,
  weekRangeLabel,
} from './planningUtils';

const STATUS_LABELS: Record<StaffingPlanStatus, { label: string; className: string }> = {
  DRAFT: {
    label: 'Roboczy',
    className: 'bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600',
  },
  PUBLISHED: {
    label: 'Opublikowany',
    className:
      'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-200 dark:border-emerald-800',
  },
  ARCHIVED: {
    label: 'Zarchiwizowany',
    className: 'bg-muted text-muted-foreground border-border',
  },
};

type ConfirmKind = 'publish' | 'unpublish' | 'delete' | null;

const FLASH_CLASSES = ['ring-4', 'ring-primary', 'ring-offset-1'];
// Hover highlight for an exception's board places (outline, so it does not
// fight with the error / warning rings on the cells).
const HIGHLIGHT_CLASSES = ['outline', 'outline-2', 'outline-primary', 'outline-offset-1'];

export const StaffingPlanPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const weekParam = searchParams.get('week');
  const weekStart = isDateString(weekParam) ? mondayOfDateString(weekParam) : mondayOf(new Date());
  const currentWeek = mondayOf(new Date());

  const canEditPerm = usePermission('planning.edit');
  const canPublishPerm = usePermission('planning.publish');
  const canAiPerm = usePermission('planning.ai');

  const { data, loading, error, refetch } = useQuery<{ staffingPlanBoard: StaffingPlanBoard }>(
    GET_STAFFING_PLAN_BOARD,
    { variables: { weekStart }, fetchPolicy: 'cache-and-network', notifyOnNetworkStatusChange: false },
  );
  const board = data?.staffingPlanBoard && data.staffingPlanBoard.weekStart === weekStart ? data.staffingPlanBoard : null;

  const [createPlan] = useMutation(CREATE_STAFFING_PLAN_MUTATION);
  const [setAssignment] = useMutation(SET_PLAN_ASSIGNMENT_MUTATION);
  const [clearAssignment] = useMutation(CLEAR_PLAN_ASSIGNMENT_MUTATION);
  const [setPin] = useMutation(SET_PLAN_ASSIGNMENT_PIN_MUTATION);
  const [publishPlan] = useMutation(PUBLISH_STAFFING_PLAN_MUTATION);
  const [unpublishPlan] = useMutation(UNPUBLISH_STAFFING_PLAN_MUTATION);
  const [deletePlan] = useMutation(DELETE_STAFFING_PLAN_MUTATION);
  const [generatePlan] = useMutation<{ generateStaffingPlan: StaffingPlanBoard }>(GENERATE_STAFFING_PLAN_MUTATION);
  const [addConstraint] = useMutation(ADD_PLAN_CONSTRAINT_MUTATION);
  const [deleteConstraint] = useMutation(DELETE_PLAN_CONSTRAINT_MUTATION);
  const [confirmConstraint] = useMutation(CONFIRM_PLAN_CONSTRAINT_MUTATION);
  const [rejectConstraint] = useMutation(REJECT_PLAN_CONSTRAINT_MUTATION);
  const [proposeFromText] = useMutation<{ proposePlanConstraintsFromText: PlanAiProposalResult }>(
    PROPOSE_PLAN_CONSTRAINTS_FROM_TEXT_MUTATION,
  );
  const { data: aiData } = useQuery<{ planningAiAvailable: boolean }>(GET_PLANNING_AI_AVAILABLE, {
    skip: !canAiPerm,
    fetchPolicy: 'cache-first',
  });
  const aiEnabled = canAiPerm && aiData?.planningAiAvailable === true;
  const [exportPlan] = useMutation<{ exportStaffingPlanToExcel: string }>(EXPORT_STAFFING_PLAN_MUTATION);

  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmKind>(null);
  const [editorCell, setEditorCell] = useState<{ employeeId: string; date: string } | null>(null);
  const [search, setSearch] = useState('');
  const [onlyPlannable, setOnlyPlannable] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Week whose exceptions changed since the plan was last re-laid.
  const [regenerateWeek, setRegenerateWeek] = useState<string | null>(null);
  const needsRegenerate = regenerateWeek === weekStart;

  const model = useMemo(() => (board ? buildBoardModel(board) : null), [board]);

  const filteredEmployees = useMemo(() => {
    if (!board) return [];
    const q = search.trim().toLocaleLowerCase('pl');
    return board.employees.filter((e) => {
      if (onlyPlannable && e.allowedSlotIds.length === 0) return false;
      if (!q) return true;
      return (
        `${e.lastName} ${e.firstName}`.toLocaleLowerCase('pl').includes(q) ||
        (e.positionName ?? '').toLocaleLowerCase('pl').includes(q) ||
        (e.departmentName ?? '').toLocaleLowerCase('pl').includes(q)
      );
    });
  }, [board, search, onlyPlannable]);

  const groups = useMemo(
    () => (model ? groupEmployees(filteredEmployees, model) : []),
    [filteredEmployees, model],
  );

  const dates: DateMeta[] = useMemo(() => {
    if (!board || !model) return [];
    const today = todayDateString();
    return board.dates.map((date) => {
      const dow = parseDateString(date).getDay();
      return {
        date,
        isToday: date === today,
        isWeekend: dow === 0 || dow === 6,
        holiday: model.holidayByDate.get(date),
        isExtraWorkDay: model.workDaySet.has(date),
      };
    });
  }, [board, model]);

  const usedSlotIds = useMemo(() => {
    const set = new Set<string>();
    board?.employees.forEach((e) => e.allowedSlotIds.forEach((id) => set.add(id)));
    board?.assignments.forEach((a) => a.shiftTimeId && set.add(a.shiftTimeId));
    return set;
  }, [board]);

  const plan = board?.plan ?? null;
  const status = plan?.status ?? null;
  const hardCount = model?.hardCount ?? 0;
  const softCount = model?.softCount ?? 0;
  const assignmentCount = board?.assignments.length ?? 0;

  const generateCounts: GenerateCounts = useMemo(() => {
    const plannable = new Set(
      (board?.employees ?? []).filter((e) => e.allowedSlotIds.length > 0).map((e) => e.employeeId),
    );
    const assignments = board?.assignments ?? [];
    const pinned = assignments.filter((a) => a.isPinned).length;
    const plannableAssigned = assignments.filter((a) => plannable.has(a.employeeId)).length;
    return {
      assigned: assignments.length,
      pinned,
      unpinned: assignments.length - pinned,
      empty: Math.max(0, plannable.size * (board?.dates.length ?? 7) - plannableAssigned),
      plannablePeople: plannable.size,
    };
  }, [board]);

  const canCreate = canEditPerm && !!board && !plan;
  const canEditCells =
    canEditPerm && !!plan && status !== 'ARCHIVED' && (status !== 'PUBLISHED' || canPublishPerm);
  const readOnlyReason: string | null = !plan
    ? 'Na ten tydzień nie ma jeszcze planu.'
    : !canEditPerm
    ? 'Nie masz uprawnień do zmiany planu — możesz go tylko przeglądać.'
    : status === 'ARCHIVED'
    ? 'Plan jest zarchiwizowany i nie można go już zmieniać.'
    : status === 'PUBLISHED' && !canPublishPerm
    ? 'Plan jest opublikowany. Zmieniać go mogą tylko osoby z uprawnieniem do publikacji.'
    : null;

  const publishBlockedReason =
    hardCount > 0
      ? `Plan ma błędy (${hardCount}). Popraw je, aby móc go opublikować — lista jest w sekcji „Problemy w planie”.`
      : assignmentCount === 0
      ? 'Plan jest pusty. Zaplanuj najpierw zmiany, aby móc go opublikować.'
      : null;

  // ------------------------------------------------------------------ week
  const goToWeek = (week: string) => {
    setEditorCell(null);
    setSearchParams(week === currentWeek ? {} : { week });
  };

  // --------------------------------------------------------------- actions
  const run = async (action: () => Promise<unknown>, success: string, fallback: string) => {
    setBusy(true);
    try {
      await action();
      toast.success(success);
      return true;
    } catch (e) {
      toast.error(errorMessage(e, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = () =>
    run(
      () => createPlan({ variables: { weekStart } }),
      'Utworzono plan na ten tydzień',
      'Nie udało się utworzyć planu',
    );

  const handleGenerate = async (mode: PlanGenerateMode) => {
    if (!plan) return;
    setBusy(true);
    try {
      const { data: result } = await generatePlan({ variables: { planId: plan.id, mode } });
      const violations = result?.generateStaffingPlan.violations ?? [];
      const hard = violations.filter((v) => v.severity === 'HARD').length;
      const soft = violations.length - hard;
      const summary = `Błędy: ${hard}, ostrzeżenia: ${soft}.`;
      if (hard > 0) toast.warning(`Plan ułożony, ale zostały problemy do poprawienia. ${summary}`);
      else toast.success(`Plan ułożony. ${summary}`);
      setGenerateOpen(false);
      if (mode === 'REGENERATE_UNPINNED') setRegenerateWeek(null);
    } catch (e) {
      toast.error(errorMessage(e, 'Nie udało się ułożyć planu'));
    } finally {
      setBusy(false);
    }
  };

  const handleAddConstraint = (input: Omit<AddPlanConstraintInput, 'planId'>) => {
    if (!plan) return Promise.resolve(false);
    return run(
      () => addConstraint({ variables: { input: { ...input, planId: plan.id } } }),
      'Dodano wyjątek',
      'Nie udało się dodać wyjątku',
    ).then((ok) => {
      if (ok) setRegenerateWeek(weekStart);
      return ok;
    });
  };

  const handleDeleteConstraint = async (c: PlanConstraintInfo) => {
    const ok = await run(
      () => deleteConstraint({ variables: { constraintId: c.id } }),
      'Usunięto wyjątek',
      'Nie udało się usunąć wyjątku',
    );
    if (ok && c.status === 'CONFIRMED') setRegenerateWeek(weekStart);
    return ok;
  };

  const handleConfirmConstraint = async (c: PlanConstraintInfo) => {
    const ok = await run(
      () => confirmConstraint({ variables: { constraintId: c.id } }),
      'Wyjątek zastosowany',
      'Nie udało się zastosować wyjątku',
    );
    if (ok) setRegenerateWeek(weekStart);
  };

  const handleRejectConstraint = (c: PlanConstraintInfo) =>
    run(
      () => rejectConstraint({ variables: { constraintId: c.id } }),
      'Propozycja odrzucona',
      'Nie udało się odrzucić propozycji',
    );

  const handleRejectAll = async (group: PlanConstraintInfo[]) => {
    setBusy(true);
    try {
      for (const c of group) {
        await rejectConstraint({ variables: { constraintId: c.id } });
      }
      toast.success('Propozycje odrzucone');
    } catch (e) {
      toast.error(errorMessage(e, 'Nie udało się odrzucić propozycji'));
    } finally {
      setBusy(false);
    }
  };

  const handlePropose = async (text: string): Promise<PlanAiProposalResult | null> => {
    if (!plan) return null;
    setBusy(true);
    try {
      const { data: result } = await proposeFromText({ variables: { input: { planId: plan.id, text } } });
      const r = result?.proposePlanConstraintsFromText ?? null;
      if (r) {
        if (r.proposedCount > 0) {
          toast.success(`Przygotowano propozycje: ${r.proposedCount}. Sprawdź je w części „Do potwierdzenia”.`);
        } else if (r.issues.length === 0) {
          toast.info('AI nie znalazło w tekście żadnych wyjątków.');
        }
      }
      return r;
    } catch (e) {
      toast.error(errorMessage(e, 'Nie udało się przetworzyć tekstu'));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    if (!plan) return;
    setExporting(true);
    try {
      const { data: result } = await exportPlan({ variables: { planId: plan.id } });
      if (result?.exportStaffingPlanToExcel) {
        downloadBase64Xlsx(result.exportStaffingPlanToExcel, `plan-obsady-${weekStart}.xlsx`);
        toast.success('Plan został wyeksportowany do pliku Excel');
      }
    } catch (e) {
      toast.error(errorMessage(e, 'Nie udało się wyeksportować planu'));
    } finally {
      setExporting(false);
    }
  };

  const handleConfirm = async () => {
    if (!plan) return;
    let ok = false;
    if (confirm === 'publish') {
      ok = await run(
        () => publishPlan({ variables: { planId: plan.id } }),
        'Plan został opublikowany',
        'Nie udało się opublikować planu',
      );
    } else if (confirm === 'unpublish') {
      ok = await run(
        () => unpublishPlan({ variables: { planId: plan.id } }),
        'Cofnięto publikację — plan jest znowu roboczy',
        'Nie udało się cofnąć publikacji',
      );
    } else if (confirm === 'delete') {
      ok = await run(
        async () => {
          await deletePlan({ variables: { planId: plan.id } });
          await refetch();
        },
        'Plan został usunięty',
        'Nie udało się usunąć planu',
      );
    }
    if (ok) setConfirm(null);
  };

  const editorEmployee = editorCell && model ? model.employeeById.get(editorCell.employeeId) : undefined;
  const editorKey = editorCell ? cellKey(editorCell.employeeId, editorCell.date) : '';

  const handleSave = async (shiftTimeId: string | null, note: string) => {
    if (!plan || !editorCell) return;
    const ok = await run(
      () =>
        setAssignment({
          variables: {
            input: {
              planId: plan.id,
              employeeId: editorCell.employeeId,
              date: editorCell.date,
              ...(shiftTimeId ? { shiftTimeId } : {}),
              ...(note ? { note } : {}),
            },
          },
        }),
      shiftTimeId ? 'Zapisano zmianę' : 'Zapisano dzień wolny',
      'Nie udało się zapisać komórki',
    );
    if (ok) setEditorCell(null);
  };

  const handleClear = async () => {
    if (!plan || !editorCell) return;
    const ok = await run(
      () =>
        clearAssignment({
          variables: { input: { planId: plan.id, employeeId: editorCell.employeeId, date: editorCell.date } },
        }),
      'Wyczyszczono komórkę',
      'Nie udało się wyczyścić komórki',
    );
    if (ok) setEditorCell(null);
  };

  const handleTogglePin = (isPinned: boolean) => {
    if (!plan || !editorCell) return;
    run(
      () =>
        setPin({
          variables: {
            input: { planId: plan.id, employeeId: editorCell.employeeId, date: editorCell.date, isPinned },
          },
        }),
      isPinned ? 'Komórka przypięta' : 'Komórka odpięta',
      'Nie udało się zmienić przypięcia',
    );
  };

  const handleCellClick = useCallback((employeeId: string, date: string) => {
    setEditorCell({ employeeId, date });
  }, []);

  // ------------------------------------------------------ violation focus
  /** Scrolls the board to the first id and flashes all of them. */
  const focusIds = (ids: string[]) => {
    if (ids.length === 0) return;
    // Make sure the targets are not hidden by the filters.
    const filtered = search.trim() !== '' || onlyPlannable;
    if (filtered) {
      setSearch('');
      setOnlyPlannable(false);
    }
    window.setTimeout(() => {
      const el = document.getElementById(ids[0]);
      if (!el) return;
      const container = el.closest<HTMLElement>('[data-testid="plan-board"]');
      if (container) {
        // Bring the whole board under the app header (keeps the day header
        // visible), then scroll inside the board to centre the target.
        container.scrollIntoView({ block: 'start' });
        const c = container.getBoundingClientRect();
        const r = el.getBoundingClientRect();
        container.scrollBy({
          top: r.top - c.top - c.height / 2 + r.height / 2,
          left: r.left - c.left - c.width / 2 + r.width / 2,
          behavior: 'smooth',
        });
      } else {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      const all = ids.map((id) => document.getElementById(id)).filter((x): x is HTMLElement => !!x);
      all.forEach((x) => x.classList.add(...FLASH_CLASSES));
      window.setTimeout(() => all.forEach((x) => x.classList.remove(...FLASH_CLASSES)), 2200);
    }, filtered ? 80 : 0);
  };

  const focusViolation = (v: PlanViolation) => {
    if (!model) return;
    let id: string | null = null;
    if (v.employeeId && v.date) id = cellDomId(v.employeeId, v.date);
    else if (v.employeeId) id = rowDomId(v.employeeId);
    else if (v.positionId && v.shiftTimeId && v.date) id = headcountDomId(v.positionId, v.shiftTimeId, v.date);
    if (!id && v.positionId) {
      const group = groupEmployees(board!.employees, model)
        .flatMap((d) => d.positions)
        .find((p) => p.positionId === v.positionId);
      if (group) id = positionDomId(group.key);
    }
    if (id) focusIds([id]);
  };

  // ------------------------------------------------------ exception focus
  const exceptionDomIds = (c: PlanConstraintInfo): string[] =>
    board
      ? exceptionTargets(c, board.dates).map((t) =>
          t.kind === 'cell' ? cellDomId(t.employeeId, t.date) : headcountDomId(t.positionId, t.shiftTimeId, t.date),
        )
      : [];

  const highlightedIds = useRef<string[]>([]);
  const clearHighlight = useCallback(() => {
    highlightedIds.current.forEach((id) => document.getElementById(id)?.classList.remove(...HIGHLIGHT_CLASSES));
    highlightedIds.current = [];
  }, []);
  const highlightException = (c: PlanConstraintInfo | null) => {
    clearHighlight();
    if (!c) return;
    const ids = exceptionDomIds(c);
    ids.forEach((id) => document.getElementById(id)?.classList.add(...HIGHLIGHT_CLASSES));
    highlightedIds.current = ids;
  };
  // A deleted exception or a re-rendered board must not leave a stale outline.
  useEffect(() => clearHighlight, [board, clearHighlight]);

  const locateException = (c: PlanConstraintInfo) => focusIds(exceptionDomIds(c));

  // --------------------------------------------------------------- render
  const statusInfo = status ? STATUS_LABELS[status] : null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <CalendarRange className="w-7 h-7 text-primary" />
          Plan obsady
        </h2>
        <p className="text-muted-foreground mt-2">
          Kto pracuje na której zmianie w wybranym tygodniu. Kliknij komórkę, aby ustawić zmianę albo dzień wolny.
        </p>
      </div>

      {/* Week navigation + status + actions */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Tooltip content="Poprzedni tydzień" side="top">
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Poprzedni tydzień"
                  onClick={() => goToWeek(addDaysToDateString(weekStart, -7))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
              </Tooltip>
              <Button
                variant="outline"
                onClick={() => goToWeek(currentWeek)}
                disabled={weekStart === currentWeek}
              >
                Bieżący tydzień
              </Button>
              <Tooltip content="Następny tydzień" side="top">
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Następny tydzień"
                  onClick={() => goToWeek(addDaysToDateString(weekStart, 7))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </Tooltip>
              <span className="ml-1 text-lg font-semibold" data-testid="plan-week-label">
                {weekRangeLabel(weekStart)}
              </span>
              <label className="ml-2 flex items-center gap-2 text-sm text-muted-foreground">
                <span className="hidden sm:inline">Przejdź do dnia:</span>
                <input
                  type="date"
                  aria-label="Przejdź do tygodnia zawierającego dzień"
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground dark:[color-scheme:dark]"
                  value={weekStart}
                  onChange={(e) => {
                    if (isDateString(e.target.value)) goToWeek(mondayOfDateString(e.target.value));
                  }}
                />
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {statusInfo ? (
                <span
                  data-testid="plan-status"
                  className={cn('inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold', statusInfo.className)}
                >
                  {statusInfo.label}
                </span>
              ) : board ? (
                <span
                  data-testid="plan-status"
                  className="inline-flex items-center rounded-full border border-dashed px-2.5 py-0.5 text-xs font-semibold text-muted-foreground"
                >
                  Brak planu
                </span>
              ) : null}
              {plan?.lastGeneratedAt && (
                <span className="text-xs text-muted-foreground" data-testid="plan-last-generated">
                  Ostatnio układany automatycznie: {formatTimestamp(plan.lastGeneratedAt)}
                </span>
              )}
              {status === 'PUBLISHED' && plan?.publishedAt && (
                <span className="text-xs text-muted-foreground">
                  Opublikował(a) {plan.publishedByName || 'nieznana osoba'}, {formatTimestamp(plan.publishedAt)}
                </span>
              )}
            </div>
          </div>

          {board && (plan || canCreate) && (
            <div className="flex flex-wrap items-center gap-2 border-t pt-3">
              {canCreate && (
                <Button onClick={handleCreate} disabled={busy}>
                  <Plus className="w-4 h-4 mr-2" />
                  Utwórz plan
                </Button>
              )}
              {canEditCells && (
                <Button onClick={() => setGenerateOpen(true)} disabled={busy} data-testid="plan-generate">
                  <Wand2 className="w-4 h-4 mr-2" />
                  Ułóż plan
                </Button>
              )}
              {canPublishPerm && status === 'DRAFT' && (
                publishBlockedReason ? (
                  <Tooltip content={<div className="max-w-xs whitespace-normal">{publishBlockedReason}</div>} side="bottom">
                    <Button disabled data-testid="plan-publish">
                      <Send className="w-4 h-4 mr-2" />
                      Opublikuj
                    </Button>
                  </Tooltip>
                ) : (
                  <Button onClick={() => setConfirm('publish')} disabled={busy} data-testid="plan-publish">
                    <Send className="w-4 h-4 mr-2" />
                    Opublikuj
                  </Button>
                )
              )}
              {canPublishPerm && status === 'PUBLISHED' && (
                <Button variant="outline" onClick={() => setConfirm('unpublish')} disabled={busy}>
                  <Undo2 className="w-4 h-4 mr-2" />
                  Cofnij publikację
                </Button>
              )}
              {plan && (
                <Button variant="outline" onClick={handleExport} disabled={exporting} data-testid="plan-export">
                  <FileDown className="w-4 h-4 mr-2" />
                  {exporting ? 'Eksportowanie...' : 'Eksportuj do Excela'}
                </Button>
              )}
              {canEditCells && (
                <Button variant="outline" onClick={() => setImportOpen(true)} disabled={busy} data-testid="plan-import">
                  <FileUp className="w-4 h-4 mr-2" />
                  Importuj z Excela
                </Button>
              )}
              {canEditPerm && plan && (
                status === 'PUBLISHED' ? (
                  <Tooltip content="Najpierw cofnij publikację, aby usunąć plan" side="bottom">
                    <Button variant="outline" disabled className="text-red-600 dark:text-red-400">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Usuń plan
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700 dark:text-red-400"
                    onClick={() => setConfirm('delete')}
                    disabled={busy}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Usuń plan
                  </Button>
                )
              )}
              {canPublishPerm && status === 'DRAFT' && publishBlockedReason && (
                <span className="text-xs text-muted-foreground" data-testid="plan-publish-blocked">
                  {publishBlockedReason}
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Body */}
      {!board && loading && (
        <p className="py-8 text-center text-muted-foreground">Ładowanie planu...</p>
      )}
      {!board && !loading && error && (
        <Card>
          <CardContent className="p-6 space-y-3 text-center">
            <p className="text-red-600 dark:text-red-400">Nie udało się wczytać planu: {errorMessage(error, 'nieznany błąd')}</p>
            <Button variant="outline" onClick={() => refetch()}>
              Spróbuj ponownie
            </Button>
          </CardContent>
        </Card>
      )}

      {board && model && (
        <>
          {!plan && (
            <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
              <span>
                Na ten tydzień nie ma jeszcze planu.{' '}
                {canEditPerm
                  ? 'Kliknij „Utwórz plan”, aby zacząć układać zmiany.'
                  : 'Plan może utworzyć osoba z uprawnieniem do edycji planu.'}
              </span>
            </div>
          )}
          {plan && readOnlyReason && (
            <div className="flex items-start gap-2 rounded-lg border bg-muted/50 px-4 py-3 text-sm">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
              <span>{readOnlyReason}</span>
            </div>
          )}
          {plan && !readOnlyReason && status === 'PUBLISHED' && (
            <div className="flex items-start gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Plan jest opublikowany — każda zmiana od razu trafia do obowiązującego planu.</span>
            </div>
          )}
          {model.noShiftCount > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                {model.noShiftCount} {model.noShiftCount === 1 ? 'osoba nie ma' : 'osób nie ma'} zmianowości, więc nie
                można {model.noShiftCount === 1 ? 'jej' : 'ich'} zaplanować (wiersze w paski). Przypisz zmianowość
                pracownikowi albo ustaw „Domyślną zmianowość” dla jego stanowiska.
              </span>
            </div>
          )}

          {plan && <ViolationsPanel model={model} onSelect={focusViolation} />}

          {plan && (
            <PlanConstraintsPanel
              board={board}
              model={model}
              canEdit={canEditCells}
              busy={busy}
              needsRegenerate={
                needsRegenerate || board.violations.some((v) => v.code.startsWith('CONSTRAINT_'))
              }
              onAdd={handleAddConstraint}
              onDelete={handleDeleteConstraint}
              onConfirm={handleConfirmConstraint}
              onReject={handleRejectConstraint}
              onRejectAll={handleRejectAll}
              aiEnabled={aiEnabled}
              onPropose={handlePropose}
              onRegenerate={() => handleGenerate('REGENERATE_UNPINNED')}
              onHighlight={highlightException}
              onLocate={locateException}
            />
          )}

          <div className="flex flex-wrap items-end gap-4">
            <div className="w-full sm:w-72">
              <Label htmlFor="plan-search" className="sr-only">
                Szukaj
              </Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input
                  id="plan-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Szukaj osoby, stanowiska, działu"
                  className="pl-8 h-9"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={onlyPlannable}
                onChange={(e) => setOnlyPlannable(e.target.checked)}
                className="h-4 w-4 accent-[#168dc8]"
              />
              Ukryj osoby bez zmianowości
            </label>
            <span className="text-sm text-muted-foreground">
              Zaplanowane komórki: {assignmentCount} · Osoby: {filteredEmployees.length}
              {filteredEmployees.length !== board.employees.length ? ` z ${board.employees.length}` : ''}
            </span>
          </div>

          <PlanLegend model={model} usedSlotIds={usedSlotIds} />

          {board.employees.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">Brak aktywnych pracowników w tym tygodniu.</p>
          ) : groups.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">Nikt nie pasuje do wyszukiwania.</p>
          ) : (
            <PlanBoard
              groups={groups}
              dates={dates}
              model={model}
              clickable={!!plan}
              onCellClick={handleCellClick}
            />
          )}

          <PlanCellEditor
            target={editorCell && editorEmployee ? { employee: editorEmployee, date: editorCell.date } : null}
            assignment={editorKey ? model.assignmentByCell.get(editorKey) : undefined}
            absence={editorKey ? model.absenceByCell.get(editorKey) : undefined}
            holidayName={editorCell ? model.holidayByDate.get(editorCell.date) : undefined}
            violations={
              editorKey
                ? [
                    ...(model.violationsByCell.get(editorKey)?.hard ?? []),
                    ...(model.violationsByCell.get(editorKey)?.soft ?? []),
                  ]
                : []
            }
            exceptions={editorKey ? model.exceptionsByCell.get(editorKey) : undefined}
            slotById={model.slotById}
            readOnlyReason={canEditCells ? null : readOnlyReason ?? 'Nie możesz zmieniać tego planu.'}
            busy={busy}
            onSave={handleSave}
            onClear={handleClear}
            onTogglePin={handleTogglePin}
            onClose={() => setEditorCell(null)}
          />
        </>
      )}

      <GeneratePlanDialog
        open={generateOpen}
        counts={generateCounts}
        busy={busy}
        onGenerate={handleGenerate}
        onClose={() => setGenerateOpen(false)}
      />
      {plan && (
        <PlanImportDialog
          open={importOpen}
          planId={plan.id}
          onClose={() => setImportOpen(false)}
          onApplied={() => undefined}
        />
      )}

      <PlanConfirmDialog
        open={confirm === 'publish'}
        title="Opublikować plan?"
        description={
          <>
            <p>
              Plan na tydzień <strong>{weekRangeLabel(weekStart)}</strong> stanie się obowiązujący.
            </p>
            <p>Później zmieniać go będą mogły tylko osoby z uprawnieniem do publikacji.</p>
            {softCount > 0 && (
              <p className="text-amber-700 dark:text-amber-300">
                Plan ma ostrzeżenia ({softCount}). Możesz go mimo to opublikować.
              </p>
            )}
          </>
        }
        confirmLabel="Opublikuj"
        busyLabel="Publikowanie..."
        busy={busy}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
      />
      <PlanConfirmDialog
        open={confirm === 'unpublish'}
        title="Cofnąć publikację?"
        description={
          <>
            <p>
              Plan na tydzień <strong>{weekRangeLabel(weekStart)}</strong> wróci do wersji roboczej i przestanie być
              obowiązujący.
            </p>
            <p>Zaplanowane zmiany zostaną zachowane.</p>
          </>
        }
        confirmLabel="Cofnij publikację"
        busyLabel="Cofanie..."
        busy={busy}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
      />
      <PlanConfirmDialog
        open={confirm === 'delete'}
        title="Usunąć plan?"
        destructive
        description={
          <>
            <p>
              Plan na tydzień <strong>{weekRangeLabel(weekStart)}</strong> zostanie trwale usunięty razem ze wszystkimi
              zaplanowanymi komórkami ({assignmentCount}).
            </p>
            <p className="font-medium text-red-700 dark:text-red-400">Tej operacji nie można cofnąć.</p>
          </>
        }
        confirmLabel="Usuń plan"
        busyLabel="Usuwanie..."
        busy={busy}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
      />
    </div>
  );
};

